import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { getOldChatHistory } from 'redux-store/aiChatsReducers/aiChatsActions';
import { ChevronDown, Loader2 } from 'lucide-react';

const AiAgentSidebar = ({ isVisible = false, onHide, onSelectChat = () => { }, selectedProject }) => {
    const [oldChats, setOldChats] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const dispatch = useDispatch();

    const chatCache = useRef({});
    const pageTracker = useRef({});
    const totalPageTracker = useRef({});
    const isInitialLoad = useRef(true);
    const scrollContainerRef = useRef(null);
    const requestLock = useRef(false);
    const scrollTimeout = useRef(null);
    const lastScrollPosition = useRef(0);
    const isScrolling = useRef(false);

    const getOldChatHistoryByProject = useCallback(async (pageNumber = 1) => {
        if (!selectedProject || requestLock.current) return;
        const projectId = selectedProject.id;

        // Don't fetch if we've already loaded this page
        if (pageNumber <= (pageTracker.current[projectId] || 0)) return;

        if (pageNumber > (totalPageTracker.current[projectId] || 1)) {
            setHasMore(false);
            return;
        }

        try {
            requestLock.current = true;
            setIsLoading(true);
            const response = await dispatch(getOldChatHistory({
                projectId,
                page: pageNumber
            })).unwrap();

            if (response) {
                totalPageTracker.current[projectId] = response.total_pages;

                if (pageNumber === 1) {
                    chatCache.current[projectId] = response.ai_chats;
                } else {
                    chatCache.current[projectId] = [
                        ...(chatCache.current[projectId] || []),
                        ...response.ai_chats
                    ];
                }

                pageTracker.current[projectId] = pageNumber;
                setOldChats(chatCache.current[projectId]);
                setHasMore(pageNumber < response.total_pages);
            }
        } catch (error) {
            console.error("Error fetching chats:", error);
        } finally {
            setIsLoading(false);
            requestLock.current = false;
        }
    }, [selectedProject, dispatch]);

    useEffect(() => {
        if (isVisible && selectedProject) {
            const projectId = selectedProject.id;

            if (!chatCache.current[projectId] || isInitialLoad.current) {
                getOldChatHistoryByProject(1);
                isInitialLoad.current = false;
            } else {
                setOldChats(chatCache.current[projectId]);
                // Restore scroll position after a short delay to ensure DOM is updated
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop = lastScrollPosition.current;
                        // Trigger scroll event after restoring position
                        handleScroll();
                    }
                }, 100);
            }
        }
    }, [isVisible, selectedProject, getOldChatHistoryByProject]);

    const handleScroll = useCallback(() => {
        if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current);
        }

        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        lastScrollPosition.current = scrollTop;
        const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100;

        if (isNearBottom && !isLoading && !requestLock.current && hasMore) {
            isScrolling.current = true;
            scrollTimeout.current = setTimeout(() => {
                if (selectedProject) {
                    const projectId = selectedProject.id;
                    const nextPage = (pageTracker.current[projectId] || 0) + 1;
                    getOldChatHistoryByProject(nextPage);
                }
                isScrolling.current = false;
            }, 100);
        }
    }, [isLoading, hasMore, selectedProject, getOldChatHistoryByProject]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Trigger initial check
            handleScroll();
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
        };
    }, [handleScroll]);

    const LoadingSpinner = () => (
        <div className="flex justify-center py-2">
            <Loader2 size={16} className='animate-spin' color='var(--text-primary)' />
        </div>
    );

    const renderChatItem = useCallback((chat) => {
        const title = chat.title || 'Untitled Chat';
        return (
            <Button
                key={chat.id}
                className={
                    `bgPrimary 
                    border-none !rounded-md text-[var(--text-primary)] hover:bg-[var(--hover-bg)] 
                    px-3 py-1 my-2 truncate transition-all duration-300
                    transform shadow-none outline-none`
                }
                onClick={() => { onSelectChat(chat.id) }}
                style={{ width: '100%', textAlign: 'left' }}
            >
                <span className='text-[12px]'>{title}</span>
            </Button>
        );
    }, [onSelectChat]);

    return (
        <div>
            <div className="p-3">
                <div
                    ref={scrollContainerRef}
                    className="chatscreen-custom-scrollbar max-h-[calc(100vh-515px)]"
                    style={{
                        overflowY: 'auto',
                        paddingRight: '0px'
                    }}
                    onScroll={handleScroll}
                >
                    {oldChats.map(renderChatItem)}
                    {isLoading && <LoadingSpinner />}
                </div>

                {hasMore && !isLoading && (
                    <div className="flex justify-center mt-2">
                        <Button
                            className="bg-[var(--background-layout)] border-none !rounded-md text-[var(--text-primary)] hover:bg-[var(--hover-bg)] px-3 py-2"
                            onClick={() => {
                                if (!isLoading && !requestLock.current && selectedProject) {
                                    const projectId = selectedProject.id;
                                    const nextPage = (pageTracker.current[projectId] || 0) + 1;
                                    getOldChatHistoryByProject(nextPage);
                                }
                            }}
                            disabled={isLoading || requestLock.current}
                        >
                            <span className='text-[12px]'><ChevronDown size={15} /> Load More</span>
                        </Button>
                    </div>
                )}
                {oldChats.length === 0 && (
                    <div className="text-center py-2 text-[var(--text-primary)] text-[12px] animate-fade-in">
                        No Old Chats Found
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiAgentSidebar;